<script>
import TeamAvailability from 'widget/components/TeamAvailability.vue';
import { mapGetters } from 'vuex';
import routerMixin from 'widget/mixins/routerMixin';
import configMixin from 'widget/mixins/configMixin';
import ArticleContainer from '../components/pageComponents/Home/Article/ArticleContainer.vue';
import ChatHeaderExpanded from '../components/ChatHeaderExpanded.vue';
import BusinessHoursModal from 'widget/components/BusinessHoursModal.vue';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';
import { IFrameHelper, RNHelper } from 'widget/helpers/utils';

export default {
  name: 'Home',
  components: {
    ArticleContainer,
    TeamAvailability,
    ChatHeaderExpanded,
    BusinessHoursModal,
    FluentIcon,
  },
  mixins: [configMixin, routerMixin],
  data() {
    return {
      showBusinessHoursModal: false,
    };
  },
  computed: {
    ...mapGetters({
      availableAgents: 'agent/availableAgents',
    }),
    conversationSize() {
      return 0;
    },
    unreadMessageCount() {
      return 0;
    },
    channelConfig() {
      const channelConfig = window.aiccWebChannel;
      if (!channelConfig.headerBackground) {
        channelConfig.headerBackground = {
          color: '#1136f0',
          fade: true,
        };
      }
      return channelConfig;
    },
    solidHeaderStyle() {
      return {
        background: this.channelConfig.headerBackground.color,
      };
    },
    gradientHeaderStyle() {
      return {
        background: `linear-gradient(180deg, ${this.channelConfig.headerBackground.color} 0%, rgba(${this.hexToRgb(this.channelConfig.headerBackground.color)}, 0) 100%)`,
      };
    },
    nonGradientHeaderStyle() {
      return {
        minHeight: '301px',
        background: this.channelConfig.headerBackground.color,
      };
    },
    businessHours() {
      const { workingHours, timezone } = this.channelConfig;
      return { workingHours, timezone };
    },
    isMobile() {
      const app = this.$root.$children?.[0] || this.$root;
      return app.isMobile || false;
    },
  },
  methods: {
    startConversation() {
      if (this.preChatFormEnabled && !this.conversationSize) {
        return this.navigateTo('prechat-form');
      }
      return this.navigateTo('messages');
    },
    hexToRgb(hex) {
      const color = hex.replace('#', '');

      let r;
      let g;
      let b;
      if (color.length === 3) {
        r = parseInt(color.charAt(0) + color.charAt(0), 16);
        g = parseInt(color.charAt(1) + color.charAt(1), 16);
        b = parseInt(color.charAt(2) + color.charAt(2), 16);
      } else {
        r = parseInt(color.substring(0, 2), 16);
        g = parseInt(color.substring(2, 4), 16);
        b = parseInt(color.substring(4, 6), 16);
      }

      return `${r}, ${g}, ${b}`;
    },
    showBusinessHours() {
      this.showBusinessHoursModal = true;
    },
    closeWindow() {
      if (IFrameHelper.isIFrame()) {
        IFrameHelper.sendMessage({ event: 'closeWindow' });
      } else if (RNHelper.isRNWebView) {
        RNHelper.sendMessage({ type: 'close-widget' });
      }
    },
  },
};
</script>

<template>
  <div class="home-root">
    <!-- 固定在右上角的关闭按钮 - 仅移动端显示 -->
    <button v-if="isMobile" class="floating-close-button" @click="closeWindow">
      <FluentIcon icon="dismiss" size="18" class="text-white" />
    </button>

    <!-- 固定渐变背景 Header -->
    <div
      v-if="channelConfig.headerBackground.fade"
      class="gradient-header-fixed"
      :style="gradientHeaderStyle"
    >
      <div class="header-solid" :style="solidHeaderStyle" />
      <div class="header-gradient" :style="gradientHeaderStyle" />
    </div>
    <div v-else class="gradient-header-fixed" :style="nonGradientHeaderStyle" />
    <!-- 可滚动内容区域 -->
    <div class="home-scroll-content">
      <ChatHeaderExpanded
        :intro-heading="channelConfig.welcomeTitle"
        :intro-body="channelConfig.welcomeTagline"
        :avatar-url="channelConfig.avatarUrl"
      />
      <TeamAvailability
        :available-agents="availableAgents"
        :has-conversation="!!conversationSize"
        :unread-count="unreadMessageCount"
        @start-conversation="startConversation"
        @show-business-hours="showBusinessHours"
      />
      <ArticleContainer />
    </div>
    <BusinessHoursModal
      :show="showBusinessHoursModal"
      :working-hours="businessHours.workingHours"
      :timezone="businessHours.timezone"
      @close="showBusinessHoursModal = false"
    />
  </div>
</template>

<style scoped>
.home-root {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.gradient-header-fixed {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 389px;
  z-index: 0;
  pointer-events: none;
}
.header-solid {
  width: 100%;
  height: 301px;
}
.header-gradient {
  width: 100%;
  height: 88px;
}
.home-scroll-content {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 24px 16px 16px 16px;
  display: flex;
  z-index: 1;
  flex-direction: column;
  gap: 16px;
}

.floating-close-button {
  position: fixed;
  top: 20px;
  right: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.floating-close-button svg {
  stroke-width: 2.5;
}

.floating-close-button:hover {
  background: rgba(0, 0, 0, 0.25);
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.floating-close-button:hover svg {
  stroke-width: 3;
}

.floating-close-button:active {
  transform: scale(0.95);
}
</style>
